import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, CheckSquare, Video, Users, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';

interface Props {
  appointments: number;
  tasks: number;
  videocalls: number;
  workgroupEvents: number;
  busyMinutes: number;
  workingMinutes: number;
}

type IconComponent = ComponentType<LucideProps>;

const CHIP_BASE = 'flex items-center gap-2 px-3 py-2 rounded-xl border bg-white dark:bg-gray-900 shadow-sm flex-shrink-0';

function Chip({
  icon: Icon, value, label, color,
}: { icon: IconComponent; value: number | string; label: string; color: string }) {
  return (
    <motion.div
      layout
      whileHover={{ y: -1 }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className={cn(CHIP_BASE, 'border-gray-200 dark:border-gray-700')}
    >
      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', color)}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="leading-tight">
        <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">{label}</div>
      </div>
    </motion.div>
  );
}

export default function CockpitDailyStats({
  appointments, tasks, videocalls, workgroupEvents, busyMinutes, workingMinutes,
}: Props) {
  const occupancyPct = workingMinutes > 0
    ? Math.min(100, Math.round((busyMinutes / workingMinutes) * 100))
    : 0;

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
      <Chip icon={CalendarIcon} value={appointments} label="Appuntamenti" color="bg-purple-500" />
      <Chip icon={CheckSquare} value={tasks} label="Task" color="bg-amber-500" />
      <Chip icon={Video} value={videocalls} label="Video Call" color="bg-emerald-500" />
      {workgroupEvents > 0 && (
        <Chip icon={Users} value={workgroupEvents} label="Eventi gruppo" color="bg-violet-500" />
      )}
      <Chip
        icon={Activity}
        value={`${occupancyPct}%`}
        label="Occupazione"
        color={occupancyPct >= 80 ? 'bg-rose-500' : occupancyPct >= 50 ? 'bg-orange-500' : 'bg-sky-500'}
      />
    </div>
  );
}
